import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2, ChevronsLeft } from 'lucide-react';
import UiEmojis from '../../../../shared/ui/UiEmojis';
import DocsCollaborativeEditor from '../../../docs/components/DocsCollaborativeEditor';
import { useDoc, useUpdateDoc } from '../../../docs/hooks/useDocsData';

// Columna 2 del Cuadro de Trabajo: Documentación (integración main→dev, epic D1.3).
// Header compacto propio (icono + título + colapso) + `DocsCollaborativeEditor` (Tiptap + Yjs,
// D2.4b) — NO el `DoctexHtml` clásico que `main` renderizaba (borrado en D2.4c). El guard
// anti-escritura y el conversor `docToTiptap` viven dentro del editor. Las escrituras
// (contenido/título/icono) van por `useUpdateDoc`, cuya invalidación de `['docs','list']`
// refresca el Índice sin un `docUpdate` manual.

// Fase 5.17 -- `toast` llega por prop (threadeado desde `ComponentTaskHoy` vía
// `taskWorkframeSlots` → `TaskWorkframePanel`).
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: sincroniza el título editable con el resultado del query del documento.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
export default function WorkframeDocColumn({
    docId,
    spaceId,
    isCollapsed,
    onToggleCollapse,
    toast,
}) {
    const { t } = useTranslation('tasks');
    const updateDoc = useUpdateDoc();
    const { data: docData, isLoading } = useDoc(docId);

    const [title, setTitle] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const pickerRef = useRef(null);
    const titleDebounceRef = useRef(null);

    useEffect(() => {
        setTitle(docData?.title || '');
    }, [docData?.id]);

    useEffect(() => () => { if (titleDebounceRef.current) clearTimeout(titleDebounceRef.current); }, []);

    useEffect(() => {
        if (!showEmojiPicker) return undefined;
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowEmojiPicker(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showEmojiPicker]);

    const displayIcon = () => {
        const icon = docData?.icon;
        if (!icon || icon === 'file') return '📄';
        if (icon === 'folder') return '📁';
        if (typeof icon === 'string' && (icon.startsWith('http') || icon.startsWith('/') || icon.startsWith('data:'))) {
            return <img src={icon} alt="" className="wf-doc-icon-img" />;
        }
        return icon;
    };

    const handleIconSelect = async (emoji) => {
        setShowEmojiPicker(false);
        try {
            await updateDoc.mutateAsync({ id: docId, payload: { icon: emoji || 'file' } });
            toast.success(t('workframe.doc.iconUpdated'));
        } catch {
            toast.error(t('workframe.doc.iconUpdateFailed'));
        }
    };

    const handleTitleChange = (newTitle) => {
        setTitle(newTitle);
        if (titleDebounceRef.current) clearTimeout(titleDebounceRef.current);
        titleDebounceRef.current = setTimeout(async () => {
            try {
                await updateDoc.mutateAsync({ id: docId, payload: { title: newTitle } });
                toast.success(t('workframe.doc.titleSaved'));
            } catch {
                toast.error(t('workframe.doc.titleSaveFailed'));
            }
        }, 600);
    };

    const handleSaveContent = useCallback(async (html) => {
        if (!docId) return;
        try {
            await updateDoc.mutateAsync({ id: docId, payload: { content: html } });
            toast.success(t('workframe.doc.textSaved'));
        } catch {
            toast.error(t('workframe.doc.textSaveFailed'));
        }
    }, [docId, updateDoc, toast, t]);

    if (!docId) {
        return (
            <div className={`wf-column wf-column-doc empty ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="wf-doc-header">
                    <button type="button" className="wf-doc-collapse-btn" onClick={onToggleCollapse} title={t('workframe.doc.collapse')}>
                        <ChevronsLeft size={14} />
                    </button>
                </div>
                <div className="wf-doc-placeholder">
                    <FileText size={36} color="#444" />
                    <p>{t('workframe.doc.placeholder')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`wf-column wf-column-doc ${isCollapsed ? 'collapsed' : ''}`}>
            {isLoading && !docData ? (
                <div className="wf-doc-loading">
                    <Loader2 size={24} className="wf-spin" />
                    <span>{t('workframe.doc.loading')}</span>
                </div>
            ) : (
                <div className="wf-doc-body">
                    <div className="wf-doc-header">
                        <button type="button" className="wf-doc-collapse-btn" onClick={onToggleCollapse} title={t('workframe.doc.collapse')}>
                            <ChevronsLeft size={14} />
                        </button>

                        <div className="wf-doc-icon-picker-wrapper" ref={pickerRef}>
                            <button
                                type="button"
                                className="wf-doc-icon-btn"
                                onClick={() => setShowEmojiPicker((prev) => !prev)}
                                title={t('workframe.doc.changeIcon')}
                            >
                                {displayIcon()}
                            </button>
                            {showEmojiPicker && (
                                <div className="wf-doc-emoji-popover">
                                    <UiEmojis onSelect={handleIconSelect} onClose={() => setShowEmojiPicker(false)} />
                                </div>
                            )}
                        </div>

                        <input
                            type="text"
                            value={title}
                            onChange={(e) => handleTitleChange(e.target.value)}
                            placeholder={t('workframe.doc.titlePlaceholder')}
                            className="wf-doc-title-input"
                        />
                    </div>

                    <div className="wf-doc-editor-container">
                        <DocsCollaborativeEditor
                            key={docId}
                            docId={docId}
                            spaceId={spaceId}
                            content={docData?.content}
                            onSave={handleSaveContent}
                            canEdit={docData?.can_edit !== false}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
